import React from 'react';

import classNames from 'classnames';

import { COVERAGE_COLOURS, money } from './overviewHelpers';

/**
 * IB-699 — "Procurement coverage" bar: committed / in-procurement-plan / still-to-be-planned
 * as proportional segments of the procurable (non-workforce) budget. The three segments
 * reconcile to the procurable budget; "in procurement plan" is the value of open procurements.
 */
const LegendItem = ({ colour, label, value }) => (
    <div className="tw-flex tw-items-center tw-gap-2">
        <span className={classNames('tw-h-3 tw-w-3 tw-rounded-sm tw-shrink-0', colour)} />
        <span className="tw-text-sm tw-text-charcoal-75">{label}</span>
        <span className="tw-text-sm tw-font-semibold tw-text-charcoal">{value}</span>
    </div>
);

const CoverageBar = ({ stats, currencySymbol }) => {
    const procurable = Number(stats?.total_non_workforce_budget) || 0;
    const committed = Number(stats?.committed_value) || 0;
    const stillToPlan = Number(stats?.still_to_plan_value) || 0;
    const inPlan = Number(stats?.in_plan_value) || 0;

    const pct = (value) => (procurable > 0 ? (value / procurable) * 100 : 0);

    const segments = [
        { key: 'committed', colour: COVERAGE_COLOURS.committed, value: committed },
        { key: 'inPlan', colour: COVERAGE_COLOURS.inPlan, value: inPlan },
        { key: 'stillToPlan', colour: COVERAGE_COLOURS.stillToPlan, value: stillToPlan },
    ];

    return (
        <div className="tw-rounded-lg tw-bg-white tw-shadow-primary tw-px-6 tw-py-6.5">
            <div className="tw-flex tw-items-baseline tw-gap-2 tw-mb-4">
                <h4 className="tw-text-base tw-font-semibold tw-text-charcoal">{$t('Procurement coverage')}</h4>
                <span className="tw-text-sm tw-text-charcoal-50">{$t('budget captured in the plan vs still to be planned')}</span>
            </div>

            <div className="tw-flex tw-w-full tw-h-6 tw-rounded-md tw-overflow-hidden tw-bg-grey">
                {segments.map((segment) => (
                    <div key={segment.key} className={segment.colour} style={{ width: `${pct(segment.value)}%` }} />
                ))}
            </div>

            <div className="tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-2 tw-mt-4">
                <LegendItem colour={COVERAGE_COLOURS.committed} label={$t('Committed')} value={money(currencySymbol, committed)} />
                <LegendItem colour={COVERAGE_COLOURS.inPlan} label={$t('In procurement plan')} value={money(currencySymbol, inPlan)} />
                <LegendItem colour={COVERAGE_COLOURS.stillToPlan} label={$t('Still to be planned')} value={money(currencySymbol, stillToPlan)} />
            </div>

            <p className="tw-mt-4 tw-text-sm tw-text-charcoal-50">
                {`${$t('Committed is money locked in with suppliers; In procurement plan is the value captured in open procurements.')} `}
                <span className="tw-font-semibold tw-text-charcoal">{`${$t('Still to be planned')} ${money(currencySymbol, stillToPlan)}`}</span>
                {` ${$t('is non-workforce budget that has no procurement record yet — this is what still needs to be planned.')}`}
            </p>
        </div>
    );
};

export default CoverageBar;
